/* ============================================================
   <spring-principles> — single-open accordion with side panel
   swap on desktop and inline expansion on mobile.

   Layout responsibilities:
   - Desktop (≥1024px): exactly one row is always open; the right
     panel cross-fades to match.
   - Mobile (<1024px): all rows start closed; clicking a row opens
     it and closes any other; clicking the open row closes it.

   Visual contract:
   - SSR renders the first row as open and uses [hidden] on the
     other panels so the section works without JS. On mount, JS
     removes [hidden] and switches to class-based active state so
     the CSS .js-ready cross-fade / expand transitions can run.
   - A brief .js-init class suppresses transitions during the very
     first apply, so the mobile "all closed" reconciliation doesn't
     animate from the SSR open state.

   Accessibility:
   - Disclosure pattern: row toggles are <button aria-expanded
     aria-controls>; panels carry ids + aria-labelledby.
   - Inactive panels and collapsed inline panels get
     aria-hidden="true"; collapsed inline panels also get `inert`
     so their CTAs drop out of the tab order (they're clipped via
     grid-template-rows: 0fr, not display: none).
   - Keyboard: Enter/Space toggles (re-opens if active on desktop;
     closes on mobile), Arrow / Home / End cycle focus among rows.
   - Reduced motion honored via CSS transition overrides.
   ============================================================ */

const MOBILE_MQ = '(max-width: 1023.98px)';

class SpringPrinciples extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('.spring-principles__row'));
    this.toggles = Array.from(this.querySelectorAll('.spring-principles__row-toggle'));
    this.panels = Array.from(this.querySelectorAll('.spring-principles__panel'));
    this.inlinePanels = Array.from(this.querySelectorAll('.spring-principles__row-inline-panel'));

    if (this.toggles.length === 0) return;

    this.mql = window.matchMedia(MOBILE_MQ);
    this.activeIndex = this.parseInitialActiveIndex();

    this.classList.add('js-init');
    this.classList.add('js-ready');

    this.panels.forEach((p) => p.removeAttribute('hidden'));
    this.inlinePanels.forEach((p) => p.removeAttribute('hidden'));

    this.applyActive(this.activeIndex);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.remove('js-init');
      });
    });

    this.bindEvents();
    this.bindViewportChange();
  }

  isMobile() {
    return this.mql.matches;
  }

  parseInitialActiveIndex() {
    if (this.isMobile()) return -1;
    const raw = parseInt(this.getAttribute('data-active-row'), 10);
    if (!Number.isNaN(raw) && raw >= 0 && raw < this.toggles.length) return raw;
    return 0;
  }

  bindEvents() {
    this.toggles.forEach((btn, idx) => {
      btn.addEventListener('click', () => {
        if (idx === this.activeIndex) {
          // On mobile, clicking the open row collapses it. On desktop,
          // one row must always be open, so this is a no-op.
          if (this.isMobile()) this.activate(-1);
          return;
        }
        this.activate(idx);
      });

      btn.addEventListener('keydown', (e) => {
        const key = e.key;

        if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
          e.preventDefault();
          if (idx === this.activeIndex) {
            if (this.isMobile()) this.activate(-1);
          } else {
            this.activate(idx);
          }
          return;
        }

        if (key === 'ArrowDown' || key === 'ArrowRight') {
          e.preventDefault();
          const next = (idx + 1) % this.toggles.length;
          this.toggles[next].focus();
        } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
          e.preventDefault();
          const prev = (idx - 1 + this.toggles.length) % this.toggles.length;
          this.toggles[prev].focus();
        } else if (key === 'Home') {
          e.preventDefault();
          this.toggles[0].focus();
        } else if (key === 'End') {
          e.preventDefault();
          this.toggles[this.toggles.length - 1].focus();
        }
      });
    });
  }

  bindViewportChange() {
    const onChange = () => {
      // Crossing into desktop with no row open would leave the right panel
      // empty; promote to first row.
      if (!this.isMobile() && this.activeIndex < 0) {
        this.activate(0);
      }
    };
    if (typeof this.mql.addEventListener === 'function') {
      this.mql.addEventListener('change', onChange);
    } else if (typeof this.mql.addListener === 'function') {
      this.mql.addListener(onChange);
    }
  }

  activate(index) {
    // Capture the collapsing panel's height before applyActive while it's still
    // fully expanded. If that panel is above the target row it will shift the
    // target upward once it collapses, so we bake the offset in now.
    let collapseOffset = 0;
    if (this.isMobile() && index >= 0 && this.activeIndex >= 0 && this.activeIndex < index) {
      const collapsingPanel = this.inlinePanels[this.activeIndex];
      if (collapsingPanel) collapseOffset = collapsingPanel.getBoundingClientRect().height;
    }

    this.activeIndex = index;
    this.setAttribute('data-active-row', String(index));
    this.applyActive(index);

    if (this.isMobile() && index >= 0) {
      this.scrollRowIntoView(index, collapseOffset);
    }
  }

  scrollRowIntoView(index, collapseOffset) {
    const row = this.rows[index];
    if (!row) return;
    requestAnimationFrame(() => {
      const header = document.querySelector('header-wrapper') || document.querySelector('header');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const rowTop = row.getBoundingClientRect().top;
      const target = window.scrollY + rowTop - headerHeight - 24 - collapseOffset;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: target, behavior: reducedMotion ? 'auto' : 'smooth' });
    });
  }

  applyActive(index) {
    this.toggles.forEach((btn, i) => {
      btn.setAttribute('aria-expanded', i === index ? 'true' : 'false');
    });

    this.rows.forEach((row, i) => {
      row.classList.toggle('is-open', i === index);
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.inlinePanels.forEach((panel, i) => {
      const isActive = i === index;
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      // Collapsed inline panels are clipped (grid-template-rows: 0fr), not
      // display: none — inert keeps their focusable CTAs out of the tab order.
      if (isActive) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });
  }
}

if (!customElements.get('spring-principles')) {
  customElements.define('spring-principles', SpringPrinciples);
}
